import React from 'react';
import { Plane, Clock, Luggage, ArrowRight } from 'lucide-react';
import { passengerRoleLabel } from '../../utils/flightBooking';

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * A flight fare saved to a flight sector while the trip awaits approval.
 *
 * Mirrors ProposedHotelCard, with one difference in emphasis: fares expire in minutes rather than
 * staying broadly stable for days, so the card is blunter about the saved price being a snapshot.
 * Nothing here is ticketed.
 */
export default function ProposedFlightCard({ option, approved = false, actions = null }) {
  if (!option) return null;

  const price = parseFloat(option.price);
  const route = [option.origin, option.destination].filter(Boolean);

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
      <div className="flex items-start gap-2">
        <Plane size={15} aria-hidden="true" className="text-amber-700 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-amber-800">
              Flight option — not ticketed
            </span>
            <span className="inline-flex items-center gap-1 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
              <Clock size={9} aria-hidden="true" />
              {approved ? 'Awaiting re-price' : 'Pending approval'}
            </span>
          </div>

          <p className="mt-1.5 text-sm font-semibold text-gray-900 flex items-center gap-1.5 flex-wrap">
            {option.airline}
            {option.flightNumber && <span className="font-mono text-gray-700">{option.flightNumber}</span>}
            {route.length === 2 && (
              <span className="inline-flex items-center gap-1 text-gray-800">
                {route[0]} <ArrowRight size={11} aria-hidden="true" /> {route[1]}
              </span>
            )}
          </p>

          <div className="mt-1 space-y-0.5 text-xs text-gray-700">
            {option.date && <p>{fmtDate(option.date)}</p>}
            {option.cabinClass && <p className="capitalize">{String(option.cabinClass).toLowerCase().replace(/_/g, ' ')}</p>}
            {option.includedBaggage && (
              <p className="flex items-center gap-1">
                <Luggage size={11} aria-hidden="true" /> {option.includedBaggage}
              </p>
            )}
            {/* A fare priced for two people shown against one name reads as simply wrong, so the
                whole manifest is listed whenever there is more than one person on it. */}
            {(option.passengers || []).length > 1 ? (
              <div>
                <p className="text-gray-900 font-medium">
                  {option.passengers.length} passengers on one booking
                </p>
                <ul className="mt-0.5 space-y-0.5">
                  {option.passengers.map((p, i) => (
                    <li key={i}>
                      Ticketing as <strong className="text-gray-900">{p.name || `Passenger ${i + 1}`}</strong>
                      {p.role && p.role !== 'traveller' && (
                        <span className="text-gray-600"> · {passengerRoleLabel(p.role).toLowerCase()}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : option.ticketName && (
              <p>
                Ticketing as <strong className="text-gray-900">{option.ticketName}</strong>
              </p>
            )}
            <p>
              <span className={option.isRefundable ? 'text-green-700' : 'text-red-700'}>
                {option.isRefundable ? 'Refundable' : 'Non-refundable'}
              </span>
              {option.savedAt && <span className="text-gray-600"> · quoted {fmtDate(option.savedAt)}</span>}
              {option.savedByName && <span className="text-gray-600"> by {option.savedByName}</span>}
            </p>
          </div>

          {!isNaN(price) && (
            <p className="mt-2 text-base font-bold text-gray-900">
              {option.currency || 'AUD'} {price.toFixed(2)}
              <span className="ml-1 text-xs font-normal text-gray-700">total, incl. taxes</span>
            </p>
          )}

          <p className="mt-2 text-xs leading-relaxed text-amber-900">
            {approved
              ? 'This trip is approved. Airline fares change by the minute, so this is re-priced live before ticketing — expect the final price to differ from the quote above.'
              : 'This price is included in the approval request. No seat is held and no payment has been taken. Ticketing unlocks once the trip is approved, at the fare available then.'}
          </p>

          {actions && <div className="mt-2.5 flex flex-wrap gap-2">{actions}</div>}
        </div>
      </div>
    </div>
  );
}
